import { createI18n } from 'vue-i18n'

import de from './locales/de.json'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import ru from './locales/ru.json'

// Same languages (and picker order) as the backend's i18n/catalog.py; native names.
export const LANGUAGE_NAMES = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
  it: 'Italiano',
} as const

export type Locale = keyof typeof LANGUAGE_NAMES

// A flag is a country, not a language: only a hint which language is active.
export const LANGUAGE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  ru: '🇷🇺',
  it: '🇮🇹',
}

export function languageFlag(locale: Locale): string {
  return LANGUAGE_FLAGS[locale]
}

export const SUPPORTED_LOCALES = Object.keys(LANGUAGE_NAMES) as Locale[]

export const DEFAULT_LOCALE: Locale = 'en'

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en, de, fr, es, ru, it },
  // vue-i18n only knows one/other by default; Russian needs one/few/many.
  pluralRules: {
    ru: (choice: number, choicesLength: number) => {
      if (choicesLength !== 3) return choice === 1 ? 0 : 1
      const n = Math.abs(choice)
      if (n % 10 === 1 && n % 100 !== 11) return 0
      if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return 1
      return 2
    },
  },
})

// "de-AT" / "DE_at" -> "de"; null if empty or unsupported.
export function normalizeLocale(code: string | null | undefined): Locale | null {
  if (!code) return null
  const primary = code.trim().toLowerCase().replace('_', '-').split('-')[0]
  return (SUPPORTED_LOCALES as string[]).includes(primary) ? (primary as Locale) : null
}

// The explicitly chosen language wins, then the Telegram client language, then English.
export function resolveLocale(chosen: string | null | undefined, telegram: string | null | undefined): Locale {
  return normalizeLocale(chosen) ?? normalizeLocale(telegram) ?? DEFAULT_LOCALE
}

export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  if (typeof document !== 'undefined') document.documentElement.lang = locale
}

// The locale to format dates in: the active UI locale.
export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale
}
